import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { globalStyles } from 'snacks-design-system';
import DeliveryDashboardBottomSheet from '../DeliveryDashboardBottomSheet';
import { DeliveryDashboardScreens } from '../../models/model-enum';
import { useDashboardUIStore } from '../../store/dashboardUIStore';
import PIEText from '@src/shared/components/PIEText';
import PIESideSheet from '@src/shared/components/PIESideSheet';
import { useQueryFetchDeliveryData } from '../../queries';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { useDeliveryDataTracking } from './tracking/use-deliverydata-tracking';
import PIETab from '@src/shared/components/PIETab';
import { PIETabItem } from '@src/shared/components/PIETabItem';
import PIETabsPanel from '@src/shared/components/PIETabsPanel';
import DashboardDeliveryDataOrderTrackingChart from './components/DashboardDeliveryDataOrderTrackingChart';
import styles from './DashboardDeliveryData.module.scss';
import DashboardDeliveryDataCourierTableList from './components/DashboardDeliveryDataCourierTableList';
import useDashboardStore from '../../hooks/use-dashboard-store';
import Scrollbars from 'react-custom-scrollbars';

export interface IStatisticsCouriers {
  courierId: string;
  firstName: string;
  lastName: string;
  trackedOrders: number;
  type: 'REGISTERED' | 'GUEST';
  cashToCollect: number;
  tipsAmount: number;
  currencyCode: string;
  topDeliveryBadge: boolean;
}

export interface IStatisticsOrders {
  currentWeek: {
    totalOrders: number;
    trackedPercentage: number;
  };
}

interface IStatisticsQuery {
  couriers: IStatisticsCouriers[];
  orders: IStatisticsOrders;
}

enum DeliveryDataTab {
  ORDERS = 'Orders',
  COURIERS = 'Couriers',
}

export const DashboardDeliveryData = ({ onError }: { onError: any }) => {
  const { xdsSpacingB, xdsSpacingD, xdsSpacingE } = globalStyles;
  const { isResolutionMobile } = useDeviceDetect();
  const { setActiveScreen, activeScreen } = useDashboardUIStore();
  const { t, i18n } = useTranslation();
  const { restaurant } = useKeycloakUserState();
  const [statisticCouriers, setStatisticsCouriers] = useState<IStatisticsCouriers[]>();
  const [statisticOrders, setStatisticsOrders] = useState<IStatisticsOrders>();
  const { trackDeliveryDataSelect } = useDeliveryDataTracking();
  const [activeTab, setActiveTab] = useState<DeliveryDataTab>(DeliveryDataTab.COURIERS);
  const { orderList } = useDashboardStore();
  const [bottomSheetMaxHeight, setBottomSheetMaxHeight] = useState<number>(0);

  useEffect(() => {
    if (activeScreen === DeliveryDashboardScreens.DELIVERY_DATA_SCREEN) {
      trackDeliveryDataSelect('delivery data select');
    }
  }, [activeScreen]);

  const { refetch } = useQueryFetchDeliveryData(restaurant?.id as any, {
    onError: () => {
      onError && onError();
      console.warn('ERROR: Fetching statistics failed');
    },

    onSuccess: (data: IStatisticsQuery) => {
      const { couriers, orders } = data;
      setStatisticsCouriers(couriers);
      setStatisticsOrders(orders);
    },
  });

  useEffect(() => {
    refetch();
  }, [orderList]);

  const renderContent = () => {
    return (
      <>
        <PIEComponentUtil
          marginTop={xdsSpacingB}
          paddingLeft={xdsSpacingB}
          paddingRight={xdsSpacingD}
          paddingTop={isResolutionMobile ? xdsSpacingB : null}
          marginBottom={!isResolutionMobile ? xdsSpacingE : xdsSpacingD}
        >
          <div className={styles.DeliveryDashboardDataTabContainer}>
            <PIETab modScrollable={true}>
              <PIETabItem
                id="selection-1"
                isSelected={activeTab === DeliveryDataTab.COURIERS}
                onClick={() => setActiveTab(DeliveryDataTab.COURIERS)}
                aria={{ controls: 'delivery-data-orders-tab-panel' }}
              >
                {t('deliverydata.DATA_TAB_COURIERS')}
              </PIETabItem>
              <PIETabItem
                id="selection-2"
                aria={{ controls: 'delivery-data-orders-tab-panel' }}
                isSelected={activeTab === DeliveryDataTab.ORDERS}
                onClick={() => setActiveTab(DeliveryDataTab.ORDERS)}
              >
                {t('deliverydata.DATA_TAB_ORDERS')}
              </PIETabItem>
            </PIETab>
          </div>
        </PIEComponentUtil>
        <PIEComponentUtil
          marginTop={xdsSpacingB}
          paddingLeft={xdsSpacingD}
          paddingRight={xdsSpacingD}
          paddingTop={isResolutionMobile ? xdsSpacingB : null}
          marginBottom={!isResolutionMobile ? xdsSpacingE : xdsSpacingD}
        >
          <Scrollbars
            style={{
              height: !isResolutionMobile
                ? 'calc(80vh - 56px)'
                : `calc(${bottomSheetMaxHeight}px - 310px)`,
            }}
            autoHide={false}
            autoHideTimeout={500}
            autoHideDuration={200}
          >
            <PIETabsPanel id="delivery-data-orders-tab-panel" isSelected={true}>
              {activeTab === DeliveryDataTab.ORDERS && (
                <DashboardDeliveryDataOrderTrackingChart
                  totalOrdersCount={
                    statisticOrders?.currentWeek.totalOrders === undefined
                      ? null
                      : statisticOrders?.currentWeek.totalOrders
                  }
                  trackedOrdersPercentage={
                    statisticOrders?.currentWeek?.trackedPercentage === undefined
                      ? null
                      : statisticOrders?.currentWeek?.trackedPercentage
                  }
                />
              )}
              {activeTab === DeliveryDataTab.COURIERS && (
                <DashboardDeliveryDataCourierTableList courierList={statisticCouriers || []} />
              )}
            </PIETabsPanel>
          </Scrollbars>
        </PIEComponentUtil>
      </>
    );
  };

  return (
    (!isResolutionMobile && (
      <PIESideSheet
        title={t('deliverydata.MENU_TITLE')}
        width={400}
        isOpenInitial={true}
        marginTop={56}
        scrollable={false}
        onClose={() => {
          setActiveScreen(DeliveryDashboardScreens.MANAGE_DELIVERY_SCREEN);
          trackDeliveryDataSelect('delivery data close');
          window.usabilla_live('trigger', `MCP-1637_${i18n.language}`);
        }}
      >
        {renderContent()}
      </PIESideSheet>
    )) || (
      <DeliveryDashboardBottomSheet
        onBottomSheetHeight={() => {}}
        initialSnap={({ maxHeight }) => {
          setBottomSheetMaxHeight(maxHeight);
          return maxHeight;
        }}
        isDetailView={true}
        showCloseButton={true}
        expandOnContentDrag={false}
        header={
          <PIEText
            size="var(--xds-font-size-20)"
            align="left"
            text={t('deliverydata.MENU_TITLE')}
            fontWeight="var(--xds-font-weight-bold)"
          ></PIEText>
        }
        onClose={() => {
          setActiveScreen(DeliveryDashboardScreens.MANAGE_DELIVERY_SCREEN);
          trackDeliveryDataSelect('delivery data close');
          window.usabilla_live('trigger', `MCP-1637_${i18n.language}`);
        }}
      >
        {renderContent()}
      </DeliveryDashboardBottomSheet>
    )
  );
};

export default DashboardDeliveryData;
